import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Zap, ArrowRight, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGIN } from "@/constants/testIds";

export default function Login() {
    const { login } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        const res = await login(email, password);
        setBusy(false);
        if (res.ok) { toast.success("Welcome back"); nav("/"); }
        else toast.error(res.error || "Login failed");
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-background">
            {/* Left visual panel */}
            <div className="hidden lg:flex relative flex-col justify-between p-12 border-r border-border bg-card overflow-hidden">
                <div className="bg-grid absolute inset-0 opacity-30 pointer-events-none" />
                <div className="relative z-10 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
                        <Zap className="h-5 w-5" strokeWidth={2.5} />
                    </div>
                    <div>
                        <div className="font-heading text-xl font-bold tracking-tight">Zyberzing</div>
                        <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">Sales CRM</div>
                    </div>
                </div>
                <div className="relative z-10 space-y-4 max-w-md">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-primary">Pipeline. Precision. Performance.</div>
                    <h1 className="font-heading text-5xl font-bold leading-[1.02] tracking-tight">
                        Close deals with <span className="text-primary">quiet confidence.</span>
                    </h1>
                    <p className="text-muted-foreground text-base max-w-sm">
                        A dense, keyboard-first CRM for teams who ship. Track every lead, every follow-up, every won deal — without the bloat.
                    </p>
                </div>
                <div className="relative z-10 grid grid-cols-3 gap-6 max-w-md">
                    {[
                        { k: "01", v: "Visual Kanban" },
                        { k: "02", v: "Role-based" },
                        { k: "03", v: "Zero friction" },
                    ].map((f) => (
                        <div key={f.k}>
                            <div className="font-mono text-xs text-muted-foreground">{f.k}</div>
                            <div className="text-sm font-medium mt-1">{f.v}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Right form */}
            <div className="flex items-center justify-center p-6 md:p-12">
                <div className="w-full max-w-sm">
                    <div className="lg:hidden mb-8 flex items-center gap-3">
                        <div className="h-10 w-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
                            <Zap className="h-5 w-5" strokeWidth={2.5} />
                        </div>
                        <div className="font-heading text-2xl font-bold tracking-tight">Zyberzing</div>
                    </div>
                    <h2 className="font-heading text-3xl font-bold tracking-tight">Sign in</h2>
                    <p className="text-sm text-muted-foreground mt-1">Access your workspace to manage leads &amp; deals.</p>
                    <form onSubmit={submit} className="mt-8 space-y-4">
                        <div className="space-y-1.5">
                            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Email</label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid={LOGIN.emailInput}
                                className="w-full h-11 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                                autoComplete="email"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label htmlFor="login-password" className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Password</label>
                            <div className="relative">
                                <input
                                    id="login-password"
                                    type={showPassword ? "text" : "password"}
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    data-testid={LOGIN.passwordInput}
                                    className="w-full h-11 pl-3 pr-12 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                                    autoComplete="current-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((visible) => !visible)}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                    aria-controls="login-password"
                                    title={showPassword ? "Hide password" : "Show password"}
                                    className="absolute inset-y-0 right-0 w-11 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
                                </button>
                            </div>
                        </div>
                        <button
                            type="submit"
                            disabled={busy}
                            data-testid={LOGIN.submitButton}
                            className="w-full h-11 rounded-md bg-primary text-primary-foreground font-medium text-sm inline-flex items-center justify-center gap-2 hover:bg-primary/90 active:scale-[0.98] transition-colors transition-transform disabled:opacity-60"
                        >
                            {busy ? "Signing in…" : (<>Continue <ArrowRight className="h-4 w-4" /></>)}
                        </button>
                    </form>
                    <div className="mt-6 text-sm text-muted-foreground">
                        No account? <Link to="/register" data-testid={LOGIN.registerLink} className="text-primary font-medium hover:underline">Create one</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
